import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackProductEvent } from "@/lib/productAnalytics";

export type ConnectedReportNotification = {
  shared_match_id: string;
  current_match_id: string;
  opponent_name: string;
  match_date: string;
  revealed_at: string;
};

const db = supabase as any;
const notificationKey = ["connected_report_notifications"] as const;

/**
 * Unread double-blind Performance Report completions for the signed-in player.
 * The backend returns rows only after BOTH players have submitted full reports.
 */
export function useConnectedReportNotifications() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...notificationKey, user?.id],
    enabled: !!user?.id,
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ConnectedReportNotification[]> => {
      const { data, error } = await db.rpc("get_my_connected_match_report_notifications");
      if (error) {
        // Rollout-safe until the Phase 2H migration is deployed.
        const message = String(error.message || "").toLowerCase();
        if (message.includes("does not exist") || message.includes("could not find the function")) return [];
        throw error;
      }
      return (data ?? []) as ConnectedReportNotification[];
    },
  });
}

export function useMarkConnectedReportNotificationRead() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sharedMatchId: string) => {
      const { data, error } = await db.rpc("mark_connected_match_report_notification_read", {
        p_shared_match_id: sharedMatchId,
      });
      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: (_data, sharedMatchId) => {
      queryClient.setQueryData<ConnectedReportNotification[]>(
        [...notificationKey, user?.id],
        (current = []) => current.filter((item) => item.shared_match_id !== sharedMatchId),
      );
      trackProductEvent("connected_performance_report_notification_opened");
    },
  });
}
